import React, { useEffect, useContext, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, Form, Input, Typography, Skeleton, Avatar, Button, Divider, Tag, Checkbox, Select, Switch } from 'antd';
import { GithubOutlined } from '@ant-design/icons';
import { RuleObject } from 'antd/es/form';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { AxiosError, AxiosResponse } from 'axios';
import MainLayout from '../../components/MainLayout';
import StatusMessages, { StatusMessage } from '../../components/StatusMessages';
import { getUserAvatarSrc } from '../../utils/accounts';
import { AuthContext, AuthContextState } from '../../context/AuthContext';
import { ThemeContext, ThemeContextState } from '../../context/ThemeContext';
import {
  APPEARANCE_SETTINGS_EDITOR_THEME_LOCALSTORAGE_KEY,
  APPEARANCE_SETTINGS_EDITOR_KEY_BINDINGS_LOCALSTORAGE_KEY
} from '../../config/constants';
import {
  getUserCommunicationSettings,
  updateUserCommunicationSettings,
  UserCommunicationSettingsResponse,
  userChangePassword
} from '../../api/accounts';
import SunImage from '../../assets/images/sun.svg';
import MoonImage from '../../assets/images/moon.svg';
import styles from './Settings.module.css';

// change password form values
export interface ChangePasswordInfo {
  currentPassword: string
  newPassword: string
  confirmNewPassword: string
};

// appearance settings
export interface AppearanceSettings {
  editorTheme: string
  editorKeyBindings: string
};

export interface SettingsProps {};

// shows user's profile and account settings
const Settings = (props: SettingsProps): JSX.Element | null => {
  // get url search params
  const [searchParams] = useSearchParams();
  // ref to avoid fetching user settings multiple times on multiple renders
  const fetchSettingsCalled = useRef<boolean>(false);
  // user communication settings state
  const [communicationSettings, setCommunicationSettings] = useState<UserCommunicationSettingsResponse | null>(null);
  // password input visibility state
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  // in progress request call state
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false);
  // change password call enabled/disabled state
  const [tabActiveKey, setTabActiveKey] = useState<string>(searchParams.get('tab') ?? 'profile');
  // status messages to display on top
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([]);
  // get refresh request function from auth context
  const { me, refreshRetryRequest } = useContext(AuthContext) as AuthContextState;
  // get theme and set theme function from theme context
  const { theme, setTheme } = useContext(ThemeContext) as ThemeContextState;
  // appearance settings state
  const [appearanceSettings, setAppearanceSettings] = useState<AppearanceSettings>({
    editorTheme: localStorage.getItem(APPEARANCE_SETTINGS_EDITOR_THEME_LOCALSTORAGE_KEY) || 'dark',
    editorKeyBindings: localStorage.getItem(APPEARANCE_SETTINGS_EDITOR_KEY_BINDINGS_LOCALSTORAGE_KEY) || 'standard'
  });

  // set document title
  useEffect(() => {
    document.title = 'Settings | Sigmoid Academy';
  }, []);

  // fetch user settings on render
  useEffect(() => {
    if (fetchSettingsCalled.current) {
      return;
    }

    fetchSettingsCalled.current = true;

    refreshRetryRequest(
      getUserCommunicationSettings
    ).then((response: AxiosResponse) => setCommunicationSettings(response.data as UserCommunicationSettingsResponse)
    ).catch((error: AxiosError) => setStatusMessages(
      // update status messages with error message on error
      (previousMessages: StatusMessage[]) => [
        {
          message: 'Unable to fetch communication settings',
          status: 'error'
        }
      ]
    ));
  }, [refreshRetryRequest]);

  // handle user login
  const handleChangePassword = (info: ChangePasswordInfo) => {
    if (requestInProgress) {
      return;
    }

    setRequestInProgress(true);
    // send password change request
    refreshRetryRequest(
      async (accessToken: string | null) => await userChangePassword(
        accessToken,
        info.currentPassword,
        info.newPassword
      )
    ).then((response: AxiosResponse) => setStatusMessages(
      // update status messages with success message on success
      (previousMessages: StatusMessage[]) => [
        {
          message: 'Password changed successfully',
          status: 'success'
        }
      ]
    )).catch((error: AxiosError) => {
      // collect list of error messages from response
      let errorStatusMessages: StatusMessage[] = [];
      for (let field in (error.response?.data as any).errors) {
        let messages = (error.response?.data as any).errors[field];

        for (let i = 0; i < messages.length; i++) {
          errorStatusMessages.push({
            message: messages[i].charAt(0).toUpperCase() + messages[i].slice(1),
            status: 'error',
          });
        }
      }

      // set list of errors as status messages
      setStatusMessages((previousMessages: StatusMessage[]) => [...errorStatusMessages]);
    }).finally(() => setRequestInProgress(false));
  };

  // handle save communication settings
  const handleSaveCommunicationSettings = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (!communicationSettings) {
      return;
    }

    if (requestInProgress) {
      return;
    }

    refreshRetryRequest(
      async (accessToken: string | null) => await updateUserCommunicationSettings(
        accessToken,
        communicationSettings
      )
    ).then((response: AxiosResponse) => setStatusMessages(
      // update status messages with success message on success
      (previousMessages: StatusMessage[]) => [
        {
          message: 'Communication preferences updated successfully',
          status: 'success'
        }
      ]
    )).catch((error: AxiosError) => setStatusMessages(
      // update status messages with error message on error
      (previousMessages: StatusMessage[]) => [
        {
          message: 'Unable to update communication preferences',
          status: 'error'
        }
      ]
    ));
  };

  // handle change appearance editor settings theme
  const handleChangeAppearanceSettingsTheme = (value: boolean): void => {
    setTheme(value ? 'light' : 'dark');
    setStatusMessages(
      // update status messages with success message on success
      (previousMessages: StatusMessage[]) => [
        {
          message: 'Theme updated successfully',
          status: 'success'
        }
      ]
    );
  };

  // handle change appearance editor settings theme
  const handleChangeAppearanceEditorSettingsTheme = (value: boolean): void => {
    setAppearanceSettings(settings => ({
      ...settings,
      editorTheme: value ? 'light' : 'dark'
    }));
    localStorage.setItem(APPEARANCE_SETTINGS_EDITOR_THEME_LOCALSTORAGE_KEY, value ? 'light' : 'dark');
    setStatusMessages(
      // update status messages with success message on success
      (previousMessages: StatusMessage[]) => [
        {
          message: 'Editor theme updated successfully',
          status: 'success'
        }
      ]
    );
  };

  // handle change appearance editor settings key bindings
  const handleChangeAppearanceEditorSettingsKeyBindings = (value: string): void => {
    setAppearanceSettings(settings => ({
      ...settings,
      keyBindings: value
    }));
    localStorage.setItem(APPEARANCE_SETTINGS_EDITOR_KEY_BINDINGS_LOCALSTORAGE_KEY, value);
    setStatusMessages(
      // update status messages with success message on success
      (previousMessages: StatusMessage[]) => [
        {
          message: 'Editor key bindings updated successfully',
          status: 'success'
        }
      ]
    );
  };

  const profileTab: React.ReactNode = (
    me ?
    (
      <Form
        name='basic'
        layout={'horizontal'}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 8 }}
        initialValues={{
          username: me.username
        }}
      >
        <Form.Item
          label={
            <Typography.Text
              strong={true}
              className={styles.formLabel}
            >
              Avatar
            </Typography.Text>
          }
          name='avatar'
        >
          <Avatar
            className={styles.avatar}
            src={getUserAvatarSrc(me.display_username)}
            size={75}
          />
        </Form.Item>
        <Form.Item
          label={
            <Typography.Text
              strong={true}
              className={styles.formLabel}
            >
              Username
            </Typography.Text>
          }
          name='username'
        >
          <Input
            name='username'
            placeholder='Enter username'
            className={styles.formInputDisabled}
            disabled={true}
          />
        </Form.Item>
        <Form.Item
          label={
            <Typography.Text
              strong={true}
              className={styles.formLabel}
            >
              GitHub
            </Typography.Text>
          }
          name='github'
        >
          <Tag className={styles.gitHubTag}>
            <GithubOutlined className={styles.gitHubIcon} />
            <span>
              {me.github_oauth_user ? me.display_username : 'Not Connected'}
            </span>
          </Tag>
        </Form.Item>
      </Form>
    ) :
    (
      <Skeleton />
    )
  );

  const accountTab: React.ReactNode = (
    me ?
    (
      <div>
        <Form
          name='basic'
          layout={'horizontal'}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 8 }}
          initialValues={{
            email: me.email
          }}
        >
          <Form.Item
            label={
              <Typography.Text
                strong={true}
                className={styles.formLabel}
              >
                Email
              </Typography.Text>
            }
            name='email'
          >
            <Input
              name='email'
              placeholder='Enter email'
              className={styles.formInputDisabled}
              disabled={true}
            />
          </Form.Item>
        </Form>
        <Divider />
        <Form
          name='basic'
          layout={'horizontal'}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 8 }}
          disabled={me.github_oauth_user ?  true : false}
          onFinish={handleChangePassword}
        >
          <Form.Item
            label={
              <Typography.Text
                strong={true}
                className={styles.formLabel}
              >
                Current Password
              </Typography.Text>
            }
            name='currentPassword'
            rules={[
              {
                required: true,
                message: (
                  <Typography.Text
                    className={styles.formAlert}
                  >
                    Please enter your current password
                  </Typography.Text>
                )
              }
            ]}
          >
            <Input.Password
              name='currentPassword'
              placeholder='Enter current password'
              visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
            />
          </Form.Item>
          <Form.Item
            label={
              <Typography.Text
                strong={true}
                className={styles.formLabel}
              >
                New Password
              </Typography.Text>
            }
            name='newPassword'
            rules={[
              {
                required: true,
                message: (
                  <Typography.Text
                    className={styles.formAlert}
                  >
                    Please enter a new password
                  </Typography.Text>
                )
              },
              ({ getFieldValue }) => ({
                message: (
                  <Typography.Text
                    className={styles.formAlert}
                  >
                    Current password and new password cannot be identical
                  </Typography.Text>
                ),
                validator: (rule: RuleObject, value: any) => {
                  if (getFieldValue('currentPassword') === value) {
                    return Promise.reject(
                      new Error('Current password and new password cannot be identical')
                    );
                  }

                  return Promise.resolve();
                }
              })
            ]}
          >
            <Input.Password
              name='newPassword'
              placeholder='Enter new password'
              visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
            />
          </Form.Item>
          <Form.Item
            label={
              <Typography.Text
                strong={true}
                className={styles.formLabel}
              >
                Confirm New Password
              </Typography.Text>
            }
            name='confirmNewPassword'
            rules={[
              {
                required: true,
                message: (
                  <Typography.Text
                    className={styles.formAlert}
                  >
                    Please confirm your new password
                  </Typography.Text>
                )
              },
              ({ getFieldValue }) => ({
                message: (
                  <Typography.Text
                    className={styles.formAlert}
                  >
                    Passwords do not match
                  </Typography.Text>
                ),
                validator: (rule: RuleObject, value: any) => {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error('Passwords do not match')
                  );
                }
              })
            ]}
          >
            <Input.Password
              name='confirmNewPassword'
              placeholder='Confirm new password'
              visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
            />
          </Form.Item>
          <Form.Item className={styles.submitButtonContainer}>
            <Button
              type='primary'
              htmlType='submit'
              loading={requestInProgress}
              className='mainButton'
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </div>
    ) :
    (
      <Skeleton />
    )
  );

  const communicationTab: React.ReactNode = (
    communicationSettings ?
    (
      <div className={styles.communicationSettingsContainer}>
        <div className={styles.communicationSettingsCheckboxContainer}>
          <Checkbox
            key={'optOutOfAll'}
            className={communicationSettings.opt_out_of_all ? styles.checkboxChecked : styles.checkboxUnchecked}
            checked={communicationSettings.opt_out_of_all}
            onChange={(e: CheckboxChangeEvent) => setCommunicationSettings(
              (settings: UserCommunicationSettingsResponse | null): UserCommunicationSettingsResponse | null => {
                if (!settings) {
                  return settings;
                }

              return {
                ...settings,
                opt_out_of_all: e.target.checked
              };
            })}
          >
            <Typography.Text
              className={styles.checkboxText}
            >
              Opt Out of All Communication
            </Typography.Text>
          </Checkbox>
        </div>
        <div className={styles.communicationSettingsCheckboxContainer}>
          <Checkbox
            key={'monthlyUpdates'}
            className={communicationSettings.monthly_updates ? styles.checkboxChecked : styles.checkboxUnchecked}
            checked={communicationSettings.monthly_updates}
            disabled={communicationSettings.opt_out_of_all}
            onChange={(e: CheckboxChangeEvent) => setCommunicationSettings(
              (settings: UserCommunicationSettingsResponse | null): UserCommunicationSettingsResponse | null => {
                if (!settings) {
                  return settings;
                }

              return {
                ...settings,
                monthly_updates: e.target.checked
              };
            })}
          >
            <Typography.Text
              className={styles.checkboxText}
            >
              Monthly Updates Emails
            </Typography.Text>
          </Checkbox>
        </div>
        <div className={styles.communicationSaveButtonContainer}>
          <Button
            type='primary'
            loading={requestInProgress}
            onClick={handleSaveCommunicationSettings}
            className='mainButton'
          >
            Save Preferences
          </Button>
        </div>
      </div>
    ) :
    (
      <Skeleton />
    )
  );

  const appearanceTab: React.ReactNode = (
    <div className={styles.appearanceSettingsContainer}>
      <div className={styles.appearanceSettingsLabelsContainer}>
        <Typography.Text
          strong={true}
          className={styles.appearanceSettingsSelectLabel}
        >
          Theme:
        </Typography.Text>
        <Typography.Text
          strong={true}
          className={styles.appearanceSettingsSelectLabel}
        >
          Editor Theme:
        </Typography.Text>
        <Typography.Text
          strong={true}
          className={styles.appearanceSettingsSelectLabel}
        >
          Editor Key Bindings:
        </Typography.Text>
      </div>
      <div className={styles.appearanceSettingsSelectsContainer}>
        <Switch
          style={{backgroundColor: theme === 'light' ? '#FFFFCC' : '#4D88FF'}}
          checked={theme === 'light'}
          checkedChildren={(
            <div className={styles.appearanceSwitchContainer}>
              <img alt='light mode' src={SunImage} width='100%' />
            </div>
          )}
          unCheckedChildren={(
            <div className={styles.appearanceSwitchContainer}>
              <img alt='dark mode' src={MoonImage} width='100%' />
            </div>
          )}
          onChange={handleChangeAppearanceSettingsTheme}
        />
        <Switch
          style={{backgroundColor: appearanceSettings.editorTheme === 'light' ? '#FFFFCC' : '#4D88FF'}}
          checked={appearanceSettings.editorTheme === 'light'}
          checkedChildren={(
            <div className={styles.appearanceSwitchContainer}>
              <img alt='light mode' src={SunImage} width='100%' />
            </div>
          )}
          unCheckedChildren={(
            <div className={styles.appearanceSwitchContainer}>
              <img alt='dark mode' src={MoonImage} width='100%' />
            </div>
          )}
          onChange={handleChangeAppearanceEditorSettingsTheme}
        />
        <Select
          defaultValue={appearanceSettings.editorKeyBindings}
          className={styles.appearanceSettingsSelect}
          onChange={handleChangeAppearanceEditorSettingsKeyBindings}
          options={[
            {
              value: 'standard',
              label: (
                <Typography.Text className={styles.appearanceSettingsSelectOption}>
                  Standard
                </Typography.Text>)
            },
            {
              value: 'vim',
              label: (
                <Typography.Text className={styles.appearanceSettingsSelectOption}>
                  Vim
                </Typography.Text>)
            }
          ]}
        />
      </div>
    </div>
  );

  return (
    <div>
      <MainLayout selectedKey='username'>
        <div className={styles.mainContainer}>
          <StatusMessages items={statusMessages} />
          <Tabs
            activeKey={tabActiveKey}
            onChange={setTabActiveKey}
            type='card'
            items={[
              {
                label: 'Profile',
                key: 'profile',
                children: profileTab
              },
              {
                label: 'Account',
                key: 'account',
                children: accountTab
              },
              {
                label: 'Communication',
                key: 'communication',
                children: communicationTab
              },
              {
                label: 'Appearance',
                key: 'appearance',
                children: appearanceTab
              }
            ]}
          />
        </div>
      </MainLayout>
    </div>
  );
};

export default Settings;
